/**
 * HOF-696 D18 — the labour app's calendar. A work day is a Europe/Berlin LOCAL
 * date (D8 `work_day`), the server's editable window is "today and the 7 days
 * before it" in Berlin time (D13), so the phone must agree with it on what
 * "today" is. `utils/today.ts` is UTC: between 00:00 and 01:00/02:00 Berlin time
 * it still names yesterday, and a worker reporting after midnight would file
 * onto the wrong day.
 *
 * Every function takes its clock or date as an argument (the page injects
 * `now`), and date-only strings are handled as calendar values in UTC so no
 * machine time zone or DST switch can shift them.
 */

export const LABOUR_TIME_ZONE = 'Europe/Berlin';
/** Today plus the seven days before it (D13). */
export const EDITABLE_DAYS = 8;

const berlinFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: LABOUR_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The Europe/Berlin calendar date of an instant, as `YYYY-MM-DD`. */
export function berlinDate(now: Date): string {
  const parts = berlinFormatter.formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function toUtcDate(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function addDays(iso: string, days: number): string {
  const d = toUtcDate(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The dates the server accepts writes for, oldest first. */
export function editableDates(today: string): string[] {
  return Array.from({ length: EDITABLE_DAYS }, (_, i) => addDays(today, i - (EDITABLE_DAYS - 1)));
}

export function isEditableDate(day: string, today: string): boolean {
  return day <= today && day >= addDays(today, -(EDITABLE_DAYS - 1));
}

export function isoWeekMonday(iso: string): string {
  const weekday = toUtcDate(iso).getUTCDay(); // 0 = Sunday
  return addDays(iso, -((weekday + 6) % 7));
}

/** Monday to Sunday of the ISO week that holds `iso`. */
export function isoWeekDates(iso: string): string[] {
  const monday = isoWeekMonday(iso);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

const LOCALES: Record<string, string> = { de: 'de-DE', en: 'en-GB', ro: 'ro-RO', pl: 'pl-PL' };

function localeFor(language: string): string {
  return LOCALES[language.split('-')[0]] ?? 'de-DE';
}

/** "Mo., 2. Nov." / "lun., 2 nov." — the date's own calendar day, in UTC. */
export function formatDayLabel(
  iso: string,
  language: string,
  options: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' },
): string {
  return new Intl.DateTimeFormat(localeFor(language), { ...options, timeZone: 'UTC' }).format(toUtcDate(iso));
}
